import React, { useState, useMemo, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';
import { notificationLog } from '../../data/notificationLog';
import { learners } from '../../data/learners';
import { notificationTemplates } from '../../data/notificationTemplates';

const { FiSearch, FiMail, FiCheckCircle, FiXCircle, FiAlertTriangle, FiSend } = FiIcons;

const triggerColors = {
  not_started: 'bg-slate-100 text-slate-600',
  due_soon: 'bg-amber-100 text-amber-700',
  overdue: 'bg-red-100 text-red-700',
  phishing_followup: 'bg-orange-100 text-orange-700',
  compliance_deadline: 'bg-violet-100 text-violet-700',
  manager_escalation: 'bg-blue-100 text-blue-700',
};

const triggerLabels = {
  not_started: 'Not Started',
  due_soon: 'Due Soon',
  overdue: 'Overdue',
  phishing_followup: 'Phishing Follow-Up',
  compliance_deadline: 'Compliance Deadline',
  manager_escalation: 'Manager Escalation',
};

const TRIGGERS = ['All', 'not_started', 'due_soon', 'overdue', 'phishing_followup', 'compliance_deadline', 'manager_escalation'];

const NotificationLogTable = () => {
  const [search, setSearch] = useState('');
  const [trigger, setTrigger] = useState('All');
  const [status, setStatus] = useState('All');
  const [liveLogs, setLiveLogs] = useState([]);

  const loadLiveLogs = async () => {
    const response = await dataFetch('read/email_logs_1730000000?limit=500');
    setLiveLogs(response?.status === 'success' ? (response?.data ?? []) : []);
  };

  useEffect(() => {
    loadLiveLogs();

    window.addEventListener('notification-log-refresh', loadLiveLogs);
    return () => window.removeEventListener('notification-log-refresh', loadLiveLogs);
  }, []);

  const enriched = useMemo(() => {
    const apiLogs = liveLogs.map(log => {
      const learner = learners.find(l => l.email?.toLowerCase() === log.email?.toLowerCase());

      return {
        id: `live-${log.id}-${log.sent_at}`,
        learnerId: learner?.id || log.email,
        learner,
        templateId: 'NT001',
        template: notificationTemplates.find(t => t.id === 'NT001'),
        trigger: 'not_started',
        sentAt: log.sent_at,
        channel: 'email',
        status: log.status,
        trainingName: 'Security Training',
        opened: false,
        clicked: false,
      };
    });

    return [...apiLogs, ...notificationLog.map(n => ({
      ...n,
      learner: learners.find(l => l.id === n.learnerId),
      template: notificationTemplates.find(t => t.id === n.templateId),
    }))];
  }, [liveLogs]);

  const filtered = useMemo(() => enriched.filter(n => {
    const name = n.learner ? `${n.learner.firstName} ${n.learner.lastName}`.toLowerCase() : '';
    const matchSearch = !search || name.includes(search.toLowerCase()) || n.trainingName.toLowerCase().includes(search.toLowerCase());
    const matchTrigger = trigger === 'All' || n.trigger === trigger;
    const matchStatus = status === 'All' || n.status === status;
    return matchSearch && matchTrigger && matchStatus;
  }), [enriched, search, trigger, status]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search learner or training…"
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
        </div>
        <div className="flex flex-wrap gap-2">
          {['All', 'Delivered', 'Failed'].map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${status === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {s}
            </button>
          ))}
          <div className="border-l border-slate-200 mx-1" />
          <select value={trigger} onChange={e => setTrigger(e.target.value)}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 focus:outline-none focus:border-blue-500">
            {TRIGGERS.map(t => <option key={t} value={t}>{t === 'All' ? 'All Triggers' : triggerLabels[t]}</option>)}
          </select>
          {(search || trigger !== 'All' || status !== 'All') && (
            <button onClick={() => { setSearch(''); setTrigger('All'); setStatus('All'); }}
              className="text-xs text-blue-600 hover:underline font-medium px-2">Clear</button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Recipient</th>
                <th className="p-4">Template</th>
                <th className="p-4">Trigger</th>
                <th className="p-4">Training</th>
                <th className="p-4">Sent At</th>
                <th className="p-4">Status</th>
                <th className="p-4">Opened</th>
                <th className="p-4">Clicked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filtered.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {row.learner ? `${row.learner.firstName[0]}${row.learner.lastName[0]}` : '?'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{row.learner ? `${row.learner.firstName} ${row.learner.lastName}` : row.learnerId}</p>
                        <p className="text-xs text-slate-400">{row.learner?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-slate-600">{row.template?.name || row.templateId}</td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${triggerColors[row.trigger] || 'bg-slate-100 text-slate-600'}`}>
                      {triggerLabels[row.trigger] || row.trigger}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-slate-600 max-w-36 truncate">{row.trainingName}</td>
                  <td className="p-4 text-xs text-slate-500">{new Date(row.sentAt).toLocaleString()}</td>
                  <td className="p-4">
                    <div className={`flex items-center gap-1.5 ${row.status === 'Delivered' ? 'text-emerald-600' : 'text-red-500'}`}>
                      <SafeIcon icon={row.status === 'Delivered' ? FiCheckCircle : FiXCircle} className="text-xs" />
                      <span className="text-xs font-semibold">{row.status}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs font-semibold ${row.opened ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {row.opened ? '✓ Yes' : '—'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs font-semibold ${row.clicked ? 'text-blue-600' : 'text-slate-400'}`}>
                      {row.clicked ? '✓ Yes' : '—'}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-slate-400 text-sm">No notifications match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          Showing {filtered.length} of {enriched.length} notifications
        </div>
      </div>
    </div>
  );
};

export default NotificationLogTable;